import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AnimatedLoadingScreen from './AnimatedLoadingScreen';
import { clearSupabaseAuthToken } from '@/utils/supabaseAuth';
import { useAdminPermissions, AdminPermission } from '@/hooks/use-admin-permissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  userType?: string | string[]; // 'filmmaker', 'client', 'admin', 'both', or an array of allowed types
  requireProfileSetup?: boolean; // Require profile setup
  requireVerification?: boolean; // New prop to require verification
  adminPermission?: AdminPermission; // For admin routes - required permission key
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  userType,
  requireProfileSetup = true,
  requireVerification = true, // By default, require verification
  adminPermission,
}) => {
  const { hasPermission, isLoading: isLoadingAdminPerms } = useAdminPermissions();
  const {
    user,
    session,
    isLoading,
    userType: currentUserType,
    checkProfileSetup,
    checkDashboardAccess,
    isVerified,
    checkVerificationStatus,
    checkRoleVerificationStatus,
  } = useAuth();
  const location = useLocation();

  const [isProfileSetup, setIsProfileSetup] = useState<boolean | null>(null);
  const [isCheckingProfile, setIsCheckingProfile] = useState(true);
  const [isCheckingVerification, setIsCheckingVerification] = useState(true);
  const [verificationStatus, setVerificationStatus] =
    useState<boolean>(isVerified);
  const [invalidSession, setInvalidSession] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');

  // Determine which dashboard type is being accessed
  const getDashboardType = (
    pathname: string
  ): 'filmmaker' | 'client' | null => {
    if (pathname.startsWith('/filmmaker')) {
      return 'filmmaker';
    } else if (pathname.startsWith('/client')) {
      return 'client';
    }
    return null;
  };

  useEffect(() => {
    const validateSession = async () => {
      try {
        if (!isLoading && (!session || !user)) {
          setInvalidSession(true);
          clearSupabaseAuthToken();
        }
      } catch (error) {
        console.error('Session validation error:', error);
        setInvalidSession(true);
      }
    };

    validateSession();
  }, [isLoading, session, user]);

  useEffect(() => {
    const runChecks = async () => {
      if (!user || !session || currentUserType === 'admin') {
        setIsCheckingProfile(false);
        setIsCheckingVerification(false);
        return;
      }

      const profilePromise = (async () => {
        if (!requireProfileSetup) return true;

        // For dual accounts, DualAccountSetup creates both profiles together
        // So we just need to check if the profile setup is complete (either profile)
        // No need to check each dashboard separately
        return checkProfileSetup();
      })();

      const verificationPromise = (async () => {
        if (!requireVerification) return true;

        if (currentUserType === 'both') {
          const dashboardType = getDashboardType(location.pathname);
          if (dashboardType) {
            return checkRoleVerificationStatus(dashboardType);
          }
        }
        return checkVerificationStatus();
      })();

      // Run both in parallel
      setLoadingMessage('Checking account requirements...');

      const [profileRes, verificationRes] = await Promise.all([
        profilePromise,
        verificationPromise,
      ]);

      setIsProfileSetup(profileRes);
      setVerificationStatus(verificationRes);
      setIsCheckingProfile(false);
      setIsCheckingVerification(false);
    };

    if (!isLoading) {
      runChecks();
    }
  }, [
    user,
    session,
    isLoading,
    currentUserType,
    location.pathname,
    requireProfileSetup,
    requireVerification,
    checkDashboardAccess,
    checkProfileSetup,
    checkVerificationStatus,
    checkRoleVerificationStatus,
  ]);

  if (isLoading || isCheckingProfile || isCheckingVerification) {
    return <AnimatedLoadingScreen message={loadingMessage} />;
  }

  if (!user || !session || invalidSession) {
    clearSupabaseAuthToken();
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Skip profile setup and verification checks for admin users
  if (currentUserType === 'admin') {
    if (userType === 'admin') {
      // Check granular admin permissions if specified
      if (adminPermission) {
        if (isLoadingAdminPerms) {
          return <AnimatedLoadingScreen message="Checking permissions..." />;
        }
        if (!hasPermission(adminPermission)) {
          return <Navigate to="/admin" replace />;
        }
      }
      return <>{children}</>;
    } else {
      return <Navigate to="/admin" replace />;
    }
  }

  const isProfileSetupPage =
    location.pathname.includes('/profile-setup') ||
    location.pathname.includes('/verify-identity') ||
    location.pathname === '/dual-account-setup';

  if (requireProfileSetup && !isProfileSetup && !isProfileSetupPage) {
    if (currentUserType === 'filmmaker') {
      return <Navigate to="/filmmaker/profile-setup/basic" replace />;
    } else if (currentUserType === 'client') {
      return <Navigate to="/client/profile-setup/basic" replace />;
    } else if (currentUserType === 'both') {
      // For dual accounts, use combined setup page
      return <Navigate to="/dual-account-setup" replace />;
    }
  }

  if (requireVerification && !verificationStatus && !isProfileSetupPage) {
    const isClientAllowed =
      (currentUserType === 'client' || currentUserType === 'both') &&
      (location.pathname === '/client/dashboard/talent' ||
        location.pathname.startsWith('/client/dashboard/talent/') ||
        location.pathname === '/client/dashboard/hiring' ||
        location.pathname.startsWith('/client/dashboard/hiring') ||
        location.pathname === '/client/dashboard/messages' ||
        location.pathname.startsWith('/client/dashboard/messages'));

    const isFilmmakerAllowed =
      (currentUserType === 'filmmaker' || currentUserType === 'both') &&
      (location.pathname === '/filmmaker/dashboard/opportunities' ||
        location.pathname.startsWith('/filmmaker/dashboard/opportunities') ||
        // Jobs board is viewable without verification; applying is gated separately
        location.pathname === '/filmmaker/dashboard/jobs' ||
        location.pathname.startsWith('/filmmaker/dashboard/jobs') ||
        location.pathname === '/filmmaker/dashboard/profile' ||
        location.pathname.startsWith('/filmmaker/dashboard/profile') ||
        location.pathname === '/filmmaker/dashboard/messages' ||
        location.pathname.startsWith('/filmmaker/dashboard/messages'));

    if (
      location.pathname === '/filmmaker/dashboard' ||
      location.pathname === '/client/dashboard'
    ) {
      // Allow access to dashboard home
    } else if (location.pathname.includes('/account')) {
      // Allow access to account pages
    } else if (isClientAllowed || isFilmmakerAllowed) {
      return <>{children}</>;
    } else {
      if (currentUserType === 'filmmaker') {
        return <Navigate to="/filmmaker/verify-identity" replace />;
      } else if (currentUserType === 'client') {
        return <Navigate to="/client/verify-identity" replace />;
      } else if (currentUserType === 'both') {
        // For dual accounts, redirect to the appropriate verification based on dashboard being accessed
        const dashboardType = getDashboardType(location.pathname);
        if (dashboardType === 'client') {
          return <Navigate to="/client/verify-identity" replace />;
        } else {
          // Default to filmmaker verification
          return <Navigate to="/filmmaker/verify-identity" replace />;
        }
      }
    }
  }

  if (
    isProfileSetupPage &&
    isProfileSetup &&
    !location.pathname.includes('/verify-identity')
  ) {
    if (currentUserType === 'filmmaker') {
      return <Navigate to="/filmmaker/dashboard" replace />;
    } else if (currentUserType === 'client') {
      return <Navigate to="/client/dashboard" replace />;
    } else if (currentUserType === 'both') {
      return <Navigate to="/filmmaker/dashboard" replace />;
    }
  }

  if (userType) {
    if (Array.isArray(userType)) {
      if (!userType.includes(currentUserType) && currentUserType !== 'both') {
        if (currentUserType === 'filmmaker') {
          return <Navigate to="/filmmaker/dashboard" replace />;
        } else if (currentUserType === 'client') {
          return <Navigate to="/client/dashboard" replace />;
        } else if (currentUserType === 'admin') {
          return <Navigate to="/admin" replace />;
        }
      }
    } else if (userType !== currentUserType && currentUserType !== 'both') {
      if (currentUserType === 'filmmaker') {
        return <Navigate to="/filmmaker/dashboard" replace />;
      } else if (currentUserType === 'client') {
        return <Navigate to="/client/dashboard" replace />;
      } else if (currentUserType === 'admin') {
        return <Navigate to="/admin" replace />;
      }
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
